import React, { useState, useEffect } from 'react';
import { FiPlus, FiEdit, FiTrash, FiAlertTriangle, FiPackage, FiTrendingUp, FiTrendingDown, FiDownload, FiEye, FiCalendar } from 'react-icons/fi';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const InventoryManagement = () => {
  const [inventory, setInventory] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [inventoryCategories, setInventoryCategories] = useState([]);
  const [inventoryHistory, setInventoryHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [showIssueModal, setShowIssueModal] = useState(false);
  const [showTransactionsModal, setShowTransactionsModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [showLowStock, setShowLowStock] = useState(false);
  const [activeTab, setActiveTab] = useState('categories'); // 'categories', 'items', 'history'
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showItemHistoryModal, setShowItemHistoryModal] = useState(false);
  const [selectedItemHistory, setSelectedItemHistory] = useState([]);
  const [selectedItemName, setSelectedItemName] = useState('');
  const [historyCurrentPage, setHistoryCurrentPage] = useState(1);
  const [historyItemsPerPage, setHistoryItemsPerPage] = useState(10);
  
  const [inventoryForm, setInventoryForm] = useState({
    name: '',
    categoryId: '',
    quantity: '',
    unit: 'kg',
    costPerUnit: '',
    minQuantity: '',
    lastRestocked: new Date().toISOString().split('T')[0],
    paymentMethod: 'cash',
    isActive: true
  });

  const [receiptForm, setReceiptForm] = useState({
    inventoryId: '',
    quantity: '',
    costPerUnit: '',
    notes: '',
    transactionDate: new Date().toISOString().split('T')[0]
  });

  const [issueForm, setIssueForm] = useState({
    inventoryId: '',
    quantity: '',
    notes: '',
    transactionDate: new Date().toISOString().split('T')[0]
  });

  const [categoryForm, setCategoryForm] = useState({
    name: '',
    isActive: true
  });

  const [filterDates, setFilterDates] = useState({
    startDate: '',
    endDate: ''
  });

  const [transactionPage, setTransactionPage] = useState(1);
  const [itemTransactions, setItemTransactions] = useState([]);
  const [itemTransactionPage, setItemTransactionPage] = useState(1);
  const [itemTransactionsPerPage, setItemTransactionsPerPage] = useState(10);
  const [transactionTypeFilter, setTransactionTypeFilter] = useState('');
  const [itemFilter, setItemFilter] = useState('');
  const [inventoryPerPage, setInventoryPerPage] = useState(10);
  const [transactionsPerPageValue, setTransactionsPerPageValue] = useState(15);

  const units = ['kg', 'g', 'l', 'ml', 'pcs', 'dozen', 'packet'];

  useEffect(() => {
    fetchInventory();
    fetchAllTransactions();
    fetchInventoryCategories();
    fetchInventoryHistory();
  }, [showLowStock]);

  const fetchInventoryCategories = async () => {
    try {
      const response = await api.get('/vendor/inventory-categories');
      setInventoryCategories(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch inventory categories:', error);
      toast.error('Failed to load inventory categories');
      // Set empty array if API fails
      setInventoryCategories([]);
    }
  };

  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedCategory) {
        await api.put(`/vendor/inventory-categories/${selectedCategory._id}`, categoryForm);
        toast.success('Category updated successfully');
      } else {
        await api.post('/vendor/inventory-categories', categoryForm);
        toast.success('Category created successfully');
      }
      setShowCategoryModal(false);
      resetCategoryForm();
      fetchInventoryCategories();
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to save category';
      toast.error(errorMessage);
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;
    try {
      await api.delete(`/vendor/inventory-categories/${categoryId}`);
      toast.success('Category deleted successfully');
      fetchInventoryCategories();
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to delete category';
      toast.error(errorMessage);
    }
  };

  const openCategoryModal = (category = null) => {
    if (category) {
      setSelectedCategory(category);
      setCategoryForm({
        name: category.name,
        isActive: category.isActive !== false
      });
    } else {
      resetCategoryForm();
    }
    setShowCategoryModal(true);
  };

  const resetCategoryForm = () => {
    setCategoryForm({
      name: '',
      isActive: true
    });
    setSelectedCategory(null);
  };

  const fetchItemHistory = async (itemId) => {
    try {
      const response = await api.get(`/vendor/inventory-history/item/${itemId}`);
      setSelectedItemHistory(response.data.data || []);
      setSelectedItemName(response.data.data[0]?.itemId?.name || 'Unknown Item');
      setShowItemHistoryModal(true);
      setHistoryCurrentPage(1); // Reset to first page
    } catch (error) {
      console.error('Error fetching item history:', error);
      toast.error('Failed to fetch item history');
    }
  };

  const fetchInventory = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (showLowStock) params.append('lowStock', 'true');
      
      const response = await api.get(`/vendor/inventory-items?${params.toString()}`);
      setInventory(response.data.data || []);
    } catch (error) {
      toast.error('Failed to fetch inventory');
    } finally {
      setLoading(false);
    }
  };

  const fetchInventoryHistory = async () => {
    try {
      const response = await api.get('/vendor/inventory-history');
      setInventoryHistory(response.data.data || []);
    } catch (error) {
      toast.error('Failed to fetch inventory history');
    }
  };

  const fetchAllTransactions = async () => {
    try {
      const params = new URLSearchParams();
      if (filterDates.startDate) params.append('startDate', filterDates.startDate);
      if (filterDates.endDate) params.append('endDate', filterDates.endDate);
      if (transactionTypeFilter) params.append('transactionType', transactionTypeFilter);
      if (itemFilter) params.append('inventoryId', itemFilter);
      
      const response = await api.get(`/vendor/inventory/transactions?${params.toString()}`);
      setTransactions(response.data.data || []);
      setTransactionPage(1);
    } catch (error) {
      toast.error('Failed to fetch transactions');
    }
  };

  const fetchItemTransactions = async (itemId) => {
    try {
      // Get last 7 days data
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 7);
      
      const params = new URLSearchParams();
      params.append('startDate', startDate.toISOString().split('T')[0]);
      params.append('endDate', endDate.toISOString().split('T')[0]);
      
      const response = await api.get(`/vendor/inventory/${itemId}/transactions?${params.toString()}`);
      setItemTransactions(response.data.data || []);
      setItemTransactionPage(1);
      setShowTransactionsModal(true);
    } catch (error) {
      toast.error('Failed to fetch item transactions');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedItem) {
        await api.put(`/vendor/inventory-items/${selectedItem._id}`, inventoryForm);
        toast.success('Item updated successfully');
      } else {
        await api.post('/vendor/inventory-items', inventoryForm);
        toast.success('Item added successfully');
      }
      setShowModal(false);
      resetForm();
      fetchInventory();
      fetchInventoryHistory();
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to save item';
      toast.error(errorMessage);
    }
  };

  const handleReceiptSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/vendor/inventory-items/${receiptForm.inventoryId}/quantity`, {
        quantity: receiptForm.quantity,
        costPerUnit: receiptForm.costPerUnit,
        transactionType: 'purchase'
      });
      toast.success('Stock receipt added successfully');
      setShowReceiptModal(false);
      resetReceiptForm();
      fetchInventory();
      fetchInventoryHistory();
      fetchAllTransactions();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to add receipt');
    }
  };

  const handleIssueSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/vendor/inventory-items/${issueForm.inventoryId}/quantity`, {
        quantity: issueForm.quantity,
        transactionType: 'sale'
      });
      toast.success('Stock issue recorded successfully');
      setShowIssueModal(false);
      resetIssueForm();
      fetchInventory();
      fetchInventoryHistory();
      fetchAllTransactions();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to record issue');
    }
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    try {
      await api.delete(`/vendor/inventory-items/${itemId}`);
      toast.success('Item deleted successfully');
      fetchInventory();
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to delete item';
      toast.error(errorMessage);
    }
  };

  const handleDownloadReport = async () => {
    try {
      const params = new URLSearchParams();
      if (filterDates.startDate) params.append('startDate', filterDates.startDate);
      if (filterDates.endDate) params.append('endDate', filterDates.endDate);
      params.append('format', 'csv');

      const response = await api.get(`/vendor/inventory/reports/generate?${params.toString()}`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `inventory-report-${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Report downloaded successfully');
    } catch (error) {
      toast.error('Failed to download report');
    }
  };

  const openModal = (item = null) => {
    if (item) {
      setSelectedItem(item);
      setInventoryForm({
        name: item.name,
        categoryId: item.categoryId?._id || item.categoryId,
        quantity: item.quantity,
        unit: item.unit,
        costPerUnit: item.costPerUnit,
        minQuantity: item.minQuantity,
        lastRestocked: item.lastRestocked?.split('T')[0] || '',
        isActive: item.isActive !== false
      });
    } else {
      resetForm();
    }
    setShowModal(true);
  };

  const openReceiptModal = (item = null) => {
    if (item) {
      setReceiptForm({
        ...receiptForm,
        inventoryId: item._id,
        costPerUnit: item.costPerUnit || ''
      });
      setSelectedItem(item);
    }
    setShowReceiptModal(true);
  };

  const openIssueModal = (item = null) => {
    if (item) {
      setIssueForm({
        ...issueForm,
        inventoryId: item._id
      });
      setSelectedItem(item);
    }
    setShowIssueModal(true);
  };

  const resetForm = () => {
    setInventoryForm({
      name: '',
      categoryId: '',
      quantity: '',
      unit: 'kg',
      costPerUnit: '',
      minQuantity: '',
      paymentMethod: 'cash',
      lastRestocked: new Date().toISOString().split('T')[0],
      isActive: true
    });
    setSelectedItem(null);
  };

  const resetReceiptForm = () => {
    setReceiptForm({
      inventoryId: '',
      quantity: '',
      costPerUnit: '',
      notes: '',
      transactionDate: new Date().toISOString().split('T')[0]
    });
    setSelectedItem(null);
  };

  const resetIssueForm = () => {
    setIssueForm({
      inventoryId: '',
      quantity: '',
      notes: '',
      transactionDate: new Date().toISOString().split('T')[0]
    });
    setSelectedItem(null);
  };

  const isLowStock = (item) => item.quantity <= item.minQuantity;

  // Pagination logic for inventory items
  const indexOfLastItem = currentPage * inventoryPerPage;
  const indexOfFirstItem = indexOfLastItem - inventoryPerPage;
  const currentItems = inventory.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(inventory.length / inventoryPerPage);

  // Pagination logic for transactions
  const indexOfLastTransaction = transactionPage * transactionsPerPageValue;
  const indexOfFirstTransaction = indexOfLastTransaction - transactionsPerPageValue;
  const currentTransactions = transactions.slice(indexOfFirstTransaction, indexOfLastTransaction);
  const totalTransactionPages = Math.ceil(transactions.length / transactionsPerPageValue);

  // Pagination logic for item transactions modal
  const indexOfLastItemTransaction = itemTransactionPage * itemTransactionsPerPage;
  const indexOfFirstItemTransaction = indexOfLastItemTransaction - itemTransactionsPerPage;
  const currentItemTransactions = itemTransactions.slice(indexOfFirstItemTransaction, indexOfLastItemTransaction);
  const totalItemTransactionPages = Math.ceil(itemTransactions.length / itemTransactionsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const paginateTransactions = (pageNumber) => setTransactionPage(pageNumber);
  const paginateItemTransactions = (pageNumber) => setItemTransactionPage(pageNumber);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-green-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-orange-50 p-4 md:p-6">
      {/* Page Header */}
      <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">Inventory Management</h1>
            <p className="text-gray-600">Track inventory items, receipts, and issues</p>
          </div>
          {/* <div className="flex flex-col sm:flex-row gap-3 items-center">
            <button 
              className="bg-green-100 hover:bg-green-200 text-green-700 px-4 py-2 rounded-lg font-medium transition-colors duration-200 flex items-center gap-2"
              onClick={handleDownloadReport}
            >
              <FiDownload size={16} />
              Download Report
            </button>
            
          </div> */}
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-2xl shadow-lg p-2 mb-6">
        <div className="flex space-x-1">
          <button
            className={`flex-1 sm:flex-none px-6 py-3 rounded-xl font-medium transition-all duration-200 flex items-center justify-center gap-2 ${
              activeTab === 'categories' 
                ? 'bg-purple-100 text-purple-700 border border-purple-200' 
                : 'text-gray-600 hover:bg-gray-100'
            }`}
            onClick={() => setActiveTab('categories')}
          >
            <FiPackage size={18} />
            Categories
          </button>

          <button
            className={`flex-1 sm:flex-none px-6 py-3 rounded-xl font-medium transition-all duration-200 flex items-center justify-center gap-2 ${
              activeTab === 'items' 
                ? 'bg-blue-100 text-blue-700 border border-blue-200' 
                : 'text-gray-600 hover:bg-gray-100'
            }`}
            onClick={() => setActiveTab('items')}
          >
            <FiPackage size={18} />
            Inventory Items
          </button>

          <button
            className={`flex-1 sm:flex-none px-6 py-3 rounded-xl font-medium transition-all duration-200 flex items-center justify-center gap-2 ${
              activeTab === 'history' 
                ? 'bg-indigo-100 text-indigo-700 border border-indigo-200' 
                : 'text-gray-600 hover:bg-gray-100'
            }`}
            onClick={() => setActiveTab('history')}
          >
            <FiCalendar size={18} />
            History
          </button>
        </div>
      </div>

      {/* History Tab */}
      {activeTab === 'history' && (
        <>
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-blue-50 to-blue-100 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Item</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Type</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Quantity</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Stock Change</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Cost</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Notes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {inventoryHistory.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((history) => (
                    <tr key={history._id} className="hover:bg-gray-50 transition-colors duration-150">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {new Date(history.transactionDate).toLocaleDateString()}
                        </div>
                        <div className="text-xs text-gray-500">
                          {new Date(history.transactionDate).toLocaleTimeString()}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {history.itemId?.name || 'Unknown Item'}
                        </div>
                        <div className="text-xs text-gray-500">
                          {history.categoryId?.name || 'Unknown Category'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          history.transactionType === 'initial' ? 'bg-indigo-100 text-indigo-800' :
                          history.transactionType === 'purchase' ? 'bg-green-100 text-green-800' :
                          history.transactionType === 'sale' ? 'bg-amber-100 text-amber-800' :
                          history.transactionType === 'adjustment' ? 'bg-purple-100 text-purple-800' :
                          history.transactionType === 'wastage' ? 'bg-red-100 text-red-800' :
                          history.transactionType === 'return' ? 'bg-cyan-100 text-cyan-800' :
                          history.transactionType === 'transfer' ? 'bg-lime-100 text-lime-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {history.transactionType.charAt(0).toUpperCase() + history.transactionType.slice(1)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {Number(history.quantity).toFixed(2)} {history.unit}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          history.newQuantity > history.previousQuantity 
                            ? 'bg-green-100 text-green-800' 
                            : history.newQuantity < history.previousQuantity
                            ? 'bg-red-100 text-red-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {history.newQuantity > history.previousQuantity 
                            ? `+${(history.newQuantity - history.previousQuantity).toFixed(2)}` 
                            : history.newQuantity < history.previousQuantity
                            ? `${(history.newQuantity - history.previousQuantity).toFixed(2)}`
                            : '0.00'
                          }
                        </span>
                        <div className="text-xs text-gray-500 mt-1">
                          {Number(history.previousQuantity).toFixed(2)} → {Number(history.newQuantity).toFixed(2)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {history.costPerUnit > 0 ? `₹${Number(history.costPerUnit).toFixed(2)}/${history.unit}` : 'N/A'}
                        </div>
                        <div className="text-xs text-gray-500">
                          {history.totalCost > 0 ? `Total: ₹${Number(history.totalCost).toFixed(2)}` : ''}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 break-words max-w-md" title={history.notes}>
                          {history.notes || '-'}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {inventoryHistory.length === 0 && (
              <div className="text-center py-12">
                <div className="text-gray-400 text-lg">No history found</div>
                <div className="text-gray-500 text-sm mt-2">Inventory movements will appear here</div>
              </div>
            )}
            
            {/* Pagination for History */}
            {inventoryHistory.length > 0 && (
              <div className="bg-gray-50 rounded-lg p-4 mt-6">
                <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <span>Show</span>
                    <select
                      value={itemsPerPage}
                      onChange={(e) => {
                        setItemsPerPage(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                      className="px-3 py-1 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                    </select>
                    <span>entries</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      className="px-3 py-1 bg-gray-200 hover:bg-gray-300 disabled:bg-gray-100 disabled:text-gray-400 text-gray-700 rounded-lg font-medium transition-colors"
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </button>
                    <span className="px-3 py-1 text-sm text-gray-600">
                      Page {currentPage} of {Math.ceil(inventoryHistory.length / itemsPerPage)}
                    </span>
                    <button
                      className="px-3 py-1 bg-gray-200 hover:bg-gray-300 disabled:bg-gray-100 disabled:text-gray-400 text-gray-700 rounded-lg font-medium transition-colors"
                      onClick={() => setCurrentPage(prev => Math.min(Math.ceil(inventoryHistory.length / itemsPerPage), prev + 1))}
                      disabled={currentPage === Math.ceil(inventoryHistory.length / itemsPerPage)}
                    >
                      Next
                    </button>
                  </div>
                  <div className="text-sm text-gray-600">
                    <span>Total: {inventoryHistory.length} records</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* Items Tab */}
      {activeTab === 'items' && (
        <>
          <div className="bg-white rounded-2xl shadow-lg p-4 mb-6">
            <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded-lg transition-colors duration-200">
                  <input
                    type="checkbox"
                    checked={showLowStock}
                    onChange={(e) => setShowLowStock(e.target.checked)}
                    className="w-4 h-4 text-green-600 focus:ring-green-500 rounded cursor-pointer"
                  />
                  <span className="text-sm font-medium text-gray-700">Show Low Stock Only</span>
                </label>
              </div>
              <button
                onClick={() => setShowModal(true)}
                className="bg-blue-100 hover:bg-blue-200 text-blue-700 px-6 py-3 rounded-xl font-medium transition-all duration-200 flex items-center gap-2"
              >
                <FiPlus size={18} />
                Add Item
              </button>
            </div>
          </div>

          {/* Inventory Table */}
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-blue-50 to-blue-100 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Item Name</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Category</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Quantity</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Unit</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Cost/Unit</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Total Value</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Min Qty</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {currentItems.map((item) => (
                    <tr key={item._id} className={`transition-colors duration-150 ${isLowStock(item) ? 'bg-orange-50' : 'hover:bg-gray-50'}`}>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <div className="text-sm font-medium text-gray-900">{item.name}</div>
                          {isLowStock(item) && (
                            <FiAlertTriangle className="text-orange-500" size={16} />
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                          {(() => {
                            // Check if category is populated from categoryId
                            if (item.categoryId && item.categoryId.name) {
                              return item.categoryId.name.charAt(0).toUpperCase() + item.categoryId.name.slice(1);
                            }
                            // Fallback to category field for backward compatibility
                            if (item.category) {
                              return item.category.charAt(0).toUpperCase() + item.category.slice(1);
                            }
                            return 'N/A';
                          })()}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">{Number(item.quantity).toFixed(2)}</td>
                      <td className="px-6 py-4 text-sm text-gray-700">{item.unit}</td>
                      <td className="px-6 py-4 text-sm text-gray-700">₹{Number(item.costPerUnit).toFixed(2)}</td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-bold text-gray-900">₹{(item.quantity * item.costPerUnit).toFixed(2)}</div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">{Number(item.minQuantity).toFixed(2)}</td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          isLowStock(item) 
                            ? 'bg-orange-100 text-orange-800' 
                            : 'bg-green-100 text-green-800'
                        }`}>
                          {isLowStock(item) ? 'Low Stock' : 'In Stock'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <button 
                          className="w-full px-4 py-3 text-left hover:bg-blue-50 transition-colors duration-200 flex items-center gap-3 text-sm font-medium text-gray-700"
                          onClick={(e) => {
                            e.stopPropagation();
                            fetchItemHistory(item._id);
                            setActiveDropdown(null);
                          }}
                        >
                          <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                          </svg>
                          History
                        </button>
                        <button 
                          className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors duration-200 flex items-center gap-3 text-sm font-medium text-gray-700"
                          onClick={(e) => {
                            e.stopPropagation();
                            openModal(item);
                            setActiveDropdown(null);
                          }}
                        >
                          <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                          </svg>
                          Edit
                        </button>
                        <div className="border-t border-gray-200"></div>
                        <button 
                          className="w-full px-4 py-3 text-left hover:bg-red-50 transition-colors duration-200 flex items-center gap-3 text-sm font-medium text-red-700"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(item._id);
                            setActiveDropdown(null);
                          }}
                        >
                          <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                          </svg>
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Pagination for Inventory Items */}
            {inventory.length > 0 && (
              <div className="pagination" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1rem', padding: '1rem', borderTop: '1px solid #e0e0e0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span>Show</span>
                  <select 
                    className="form-control" 
                    value={inventoryPerPage}
                    onChange={(e) => {
                      setInventoryPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    style={{ width: 'auto', display: 'inline-block' }}
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                  <span>entries</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <button 
                    className="btn btn-secondary btn-sm" 
                    onClick={() => paginate(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </button>
                  <span style={{ padding: '0 1rem' }}>Page {currentPage} of {totalPages}</span>
                  <button 
                    className="btn btn-secondary btn-sm" 
                    onClick={() => paginate(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </button>
                </div>
                <div>
                  <span>Total: {inventory.length} items</span>
                </div>
              </div>
            )}
            
            {inventory.length === 0 && (
              <div className="empty-state">
                <p>{showLowStock ? 'No low stock items' : 'No inventory items found'}</p>
                {!showLowStock && (
                  <button className="btn btn-primary" onClick={() => openModal()}>
                    <FiPlus /> Add Your First Item
                  </button>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {/* Categories Tab */}
      {activeTab === 'categories' && (
        <>
          <div className="mb-4 flex justify-end">
            <button 
              className="px-4 py-2 bg-purple-100 hover:bg-purple-200 text-purple-700 rounded-lg transition-colors font-medium text-sm flex items-center gap-2"
              onClick={() => openCategoryModal()}
            >
              <FiPlus /> Add Category
            </button>
            <button 
              className="bg-blue-100 hover:bg-blue-200 text-blue-700 px-6 py-3 rounded-xl font-medium transition-all duration-200 flex items-center gap-2"
              onClick={() => openModal()}
            >
              <FiPlus className="text-lg" />
              Add Item
            </button>
          </div>

          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {inventoryCategories.map((category) => (
                    <tr key={category._id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-semibold text-gray-900">{category.name}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          category.isActive 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {category.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="flex space-x-2 justify-end">
                          <button
                            className="px-3 py-1 bg-blue-100 hover:bg-blue-200 text-blue-700 text-sm rounded transition-colors"
                            onClick={() => {
                              openModal();
                              // Pre-select this category
                              setInventoryForm(prev => ({ ...prev, categoryId: category._id }));
                            }}
                          >
                            Add Item
                          </button>
                          <button
                            className="px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm rounded transition-colors"
                            onClick={() => openCategoryModal(category)}
                          >
                            Edit
                          </button>
                          <button
                            className="px-3 py-1 bg-red-100 hover:bg-red-200 text-red-700 text-sm rounded transition-colors"
                            onClick={() => handleDeleteCategory(category._id)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {inventoryCategories.length === 0 && (
              <div className="text-center py-12">
                <div className="text-gray-400 text-lg">No categories found</div>
                <div className="text-gray-500 text-sm mt-2">Create your first inventory category to get started</div>
                <button 
                  className="mt-4 px-4 py-2 bg-purple-100 hover:bg-purple-200 text-purple-700 rounded-lg transition-colors font-medium text-sm flex items-center gap-2 mx-auto"
                  onClick={() => openCategoryModal()}
                >
                  <FiPlus /> Add Your First Category
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {/* Transactions Tab */}
      {activeTab === 'transactions' && (
        <>
          <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div className="form-group" style={{ margin: 0 }}>
              <label>Start Date</label>
              <input
                type="date"
                className="form-control"
                value={filterDates.startDate}
                onChange={(e) => setFilterDates({ ...filterDates, startDate: e.target.value })}
              />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label>End Date</label>
              <input
                type="date"
                className="form-control"
                value={filterDates.endDate}
                onChange={(e) => setFilterDates({ ...filterDates, endDate: e.target.value })}
              />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label>Transaction Type</label>
              <select
                className="form-control"
                value={transactionTypeFilter}
                onChange={(e) => setTransactionTypeFilter(e.target.value)}
              >
                <option value="">All Types</option>
                <option value="receipt">Receipt</option>
                <option value="issue">Issue</option>
              </select>
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label>Filter by Item</label>
              <select
                className="form-control"
                value={itemFilter}
                onChange={(e) => setItemFilter(e.target.value)}
              >
                <option value="">All Items</option>
                {inventory.map(item => (
                  <option key={item._id} value={item._id}>{item.itemName}</option>
                ))}
              </select>
            </div>
            <button 
              className="btn btn-primary" 
              onClick={fetchAllTransactions}
            >
              Apply Filters
            </button>
            <button 
              className="btn btn-secondary" 
              onClick={() => {
                setFilterDates({ startDate: '', endDate: '' });
                setTransactionTypeFilter('');
                setItemFilter('');
                fetchAllTransactions();
              }}
            >
              Clear Filters
            </button>
          </div>

          <div className="table-section">
            <div className="table-responsive">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Item Name</th>
                    <th>Type</th>
                    <th>Quantity</th>
                    <th>Cost/Unit</th>
                    <th>Total Cost</th>
                    {/* <th>Supplier/Issued To</th> */}
                    {/* <th>Purpose/Invoice</th> */}
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {currentTransactions.map((txn) => (
                    <tr key={txn._id}>
                      <td>{new Date(txn.transactionDate).toLocaleDateString()}</td>
                      <td><strong>{txn.inventoryId?.itemName}</strong></td>
                      <td>
                        {txn.transactionType === 'receipt' ? (
                          <span className="badge badge-success">
                            <FiTrendingUp /> Receipt
                          </span>
                        ) : (
                          <span className="badge badge-warning">
                            <FiTrendingDown /> Issue
                          </span>
                        )}
                      </td>
                      <td>{txn.quantity} {txn.unit}</td>
                      <td>
                        {txn.priceChange !== 0 ? (
                          <div className="flex items-center gap-2">
                            <span
                              style={{
                                color: txn.priceChange > 0 ? '#ef4444' : '#22c55e',
                                fontWeight: '600'
                              }}
                            >
                              ₹{txn.costPerUnit}
                            </span>
                            {txn.priceChange > 0 ? (
                              <span style={{ color: '#ef4444', fontSize: '12px' }}>
                                ↑ +₹{Math.abs(txn.priceChange)}
                              </span>
                            ) : (
                              <span style={{ color: '#22c55e', fontSize: '12px' }}>
                                ↓ -₹{Math.abs(txn.priceChange)}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span style={{ color: '#666' }}>₹{txn.costPerUnit}</span>
                        )}
                      </td>
                      <td><strong>₹{txn.totalCost?.toFixed(2)}</strong></td>
                      {/* <td>{txn.supplier || txn.issuedTo || 'N/A'}</td> */}
                      {/* <td>{txn.purpose || txn.invoiceNumber || 'N/A'}</td> */}
                      <td className="break-words max-w-md">{txn.notes || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Pagination for Transactions */}
            {transactions.length > 0 && (
              <div className="pagination" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1rem', padding: '1rem', borderTop: '1px solid #e0e0e0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span>Show</span>
                  <select 
                    className="form-control" 
                    value={transactionsPerPageValue}
                    onChange={(e) => {
                      setTransactionsPerPageValue(Number(e.target.value));
                      setTransactionPage(1);
                    }}
                    style={{ width: 'auto', display: 'inline-block' }}
                  >
                    <option value={10}>10</option>
                    <option value={15}>15</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                  <span>entries</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <button 
                    className="btn btn-secondary btn-sm" 
                    onClick={() => paginateTransactions(transactionPage - 1)}
                    disabled={transactionPage === 1}
                  >
                    Previous
                  </button>
                  <span style={{ padding: '0 1rem' }}>Page {transactionPage} of {totalTransactionPages}</span>
                  <button 
                    className="btn btn-secondary btn-sm" 
                    onClick={() => paginateTransactions(transactionPage + 1)}
                    disabled={transactionPage === totalTransactionPages}
                  >
                    Next
                  </button>
                </div>
                <div>
                  <span>Total: {transactions.length} transactions</span>
                </div>
              </div>
            )}
            
            {transactions.length === 0 && (
              <div className="empty-state">
                <p>No transactions found</p>
              </div>
            )}
          </div>
        </>
      )}

      {/* Add/Edit Item Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="bg-blue-100 text-blue-800 p-6 border-b border-blue-200">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold">{selectedItem ? 'Edit Item' : 'Add Item'}</h2>
                <button 
                  className="text-blue-600 hover:bg-blue-200 rounded-lg p-2 transition-colors duration-200"
                  onClick={() => setShowModal(false)}
                >
                  <FiPlus className="text-xl rotate-45" />
                </button>
              </div>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Item Name *</label>
                    <input
                      type="text"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none transition-colors duration-200"
                      value={inventoryForm.name}
                      onChange={(e) => setInventoryForm({ ...inventoryForm, name: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Category *</label>
                    <div className="flex gap-2">
                      <select
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none transition-colors duration-200"
                        value={inventoryForm.categoryId}
                        onChange={(e) => {
                          setInventoryForm({ 
                            ...inventoryForm, 
                            categoryId: e.target.value
                          });
                        }}
                        required
                      >
                        <option value="">Select Category</option>
                        {inventoryCategories.filter(cat => cat.isActive).map(cat => (
                          <option key={cat._id} value={cat._id}>{cat.name.charAt(0).toUpperCase() + cat.name.slice(1)}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => setShowCategoryModal(true)}
                        className="px-3 py-2 bg-purple-100 hover:bg-purple-200 text-purple-700 rounded-lg font-medium transition-colors duration-200 flex items-center gap-1"
                        title="Add New Category"
                      >
                        <FiPlus size={16} />
                        Add
                      </button>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Quantity *</label>
                    <input
                      type="text"
                      inputMode="decimal"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none transition-colors duration-200"
                      value={inventoryForm.quantity}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
                          setInventoryForm({ ...inventoryForm, quantity: value });
                        }
                      }}
                      required
                      disabled={selectedItem ? true : false}
                      style={selectedItem ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : {}}
                    />
                    {selectedItem && (
                      <small style={{ color: '#666', fontSize: '0.85rem' }}>
                        Use "Add Stock" or "Issue Stock" to change quantity
                      </small>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Unit *</label>
                    <select
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none transition-colors duration-200"
                      value={inventoryForm.unit}
                      onChange={(e) => setInventoryForm({ ...inventoryForm, unit: e.target.value })}
                      required
                    >
                      {units.map(unit => (
                        <option key={unit} value={unit}>{unit}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Cost per Unit (₹) *</label>
                    <input
                      type="text"
                      inputMode="decimal"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none transition-colors duration-200"
                      value={inventoryForm.costPerUnit}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
                          setInventoryForm({ ...inventoryForm, costPerUnit: value });
                        }
                      }}
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Min Stock Threshold *</label>
                    <input
                      type="text"
                      inputMode="decimal"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none transition-colors duration-200"
                      value={inventoryForm.minQuantity}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
                          setInventoryForm({ ...inventoryForm, minQuantity: value });
                        }
                      }}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Last Restocked</label>
                    <input
                      type="date"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none transition-colors duration-200"
                      value={inventoryForm.lastRestocked}
                      onChange={(e) => setInventoryForm({ ...inventoryForm, lastRestocked: e.target.value })}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Payment Method *</label>
                    <select
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none transition-colors duration-200"
                      value={inventoryForm.paymentMethod || 'cash'}
                      onChange={(e) => setInventoryForm({ ...inventoryForm, paymentMethod: e.target.value })}
                      required
                    >
                      <option value="cash">Cash</option>
                      <option value="online">Online</option>
                    </select>
                  </div>
                  <div className="flex items-center justify-end">
                    <label className="text-sm font-medium text-gray-700 mr-3">Active</label>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={inventoryForm.isActive}
                        onChange={(e) => setInventoryForm({ ...inventoryForm, isActive: e.target.checked })}
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                </div>

                {inventoryForm.quantity && inventoryForm.costPerUnit && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">Total Value:</span>
                      <span className="text-lg font-bold text-green-700">₹{(inventoryForm.quantity * inventoryForm.costPerUnit).toFixed(2)}</span>
                    </div>
                  </div>
                )}
              </div>
              <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
                <button 
                  type="button" 
                  className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium transition-colors duration-200"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="px-4 py-2 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg font-medium transition-colors duration-200"
                >
                  {selectedItem ? 'Update' : 'Add'} Item
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Receipt Modal */}
      {showReceiptModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-green-500 to-green-600 text-white p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold">Add Stock Receipt - {selectedItem?.itemName}</h2>
                <button 
                  className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-colors duration-200"
                  onClick={() => setShowReceiptModal(false)}
                >
                  <FiPlus className="text-xl rotate-45" />
                </button>
              </div>
            </div>
            <form onSubmit={handleReceiptSubmit}>
              <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Quantity *</label>
                    <input
                      type="text"
                      inputMode="decimal"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-green-500 focus:outline-none transition-colors duration-200"
                      value={receiptForm.quantity}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
                          setReceiptForm({ ...receiptForm, quantity: value });
                        }
                      }}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Cost per Unit (₹) *</label>
                    <input
                      type="text"
                      inputMode="decimal"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-green-500 focus:outline-none transition-colors duration-200"
                      value={receiptForm.costPerUnit}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
                          setReceiptForm({ ...receiptForm, costPerUnit: value });
                        }
                      }}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Transaction Date *</label>
                    <input
                      type="date"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-green-500 focus:outline-none transition-colors duration-200"
                      value={receiptForm.transactionDate}
                      onChange={(e) => setReceiptForm({ ...receiptForm, transactionDate: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                  <textarea
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-green-500 focus:outline-none transition-colors duration-200"
                    value={receiptForm.notes}
                    onChange={(e) => setReceiptForm({ ...receiptForm, notes: e.target.value })}
                    placeholder="Additional notes"
                    rows="3"
                  />
                </div>

                {receiptForm.quantity && receiptForm.costPerUnit && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">Total Cost:</span>
                      <span className="text-lg font-bold text-green-700">₹{(receiptForm.quantity * receiptForm.costPerUnit).toFixed(2)}</span>
                    </div>
                  </div>
                )}
              </div>
              <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
                <button 
                  type="button" 
                  className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium transition-colors duration-200"
                  onClick={() => setShowReceiptModal(false)}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium transition-colors duration-200"
                >
                  Add Receipt
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Issue Stock Modal */}
      {showIssueModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold">Issue Stock - {selectedItem?.itemName}</h2>
                <button 
                  className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-colors duration-200"
                  onClick={() => setShowIssueModal(false)}
                >
                  <FiPlus className="text-xl rotate-45" />
                </button>
              </div>
            </div>
            <form onSubmit={handleIssueSubmit}>
              <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Available Stock:</span>
                    <span className="text-lg font-bold text-orange-700">{selectedItem?.quantity} {selectedItem?.unit}</span>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Quantity to Issue *</label>
                    <input
                      type="text"
                      inputMode="decimal"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-orange-500 focus:outline-none transition-colors duration-200"
                      value={issueForm.quantity}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
                          setIssueForm({ ...issueForm, quantity: value });
                        }
                      }}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Transaction Date *</label>
                    <input
                      type="date"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-orange-500 focus:outline-none transition-colors duration-200"
                      value={issueForm.transactionDate}
                      onChange={(e) => setIssueForm({ ...issueForm, transactionDate: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                  <textarea
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-orange-500 focus:outline-none transition-colors duration-200"
                    value={issueForm.notes}
                    onChange={(e) => setIssueForm({ ...issueForm, notes: e.target.value })}
                    placeholder="Additional notes"
                    rows="3"
                  />
                </div>
              </div>
              <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
                <button 
                  type="button" 
                  className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium transition-colors duration-200"
                  onClick={() => setShowIssueModal(false)}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-medium transition-colors duration-200"
                >
                  Issue Stock
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Item Transactions Modal */}
      {showTransactionsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold">Transaction History</h2>
                <button 
                  className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-colors duration-200"
                  onClick={() => setShowTransactionsModal(false)}
                >
                  <FiPlus className="text-xl rotate-45" />
                </button>
              </div>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gradient-to-r from-blue-50 to-blue-100 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Date</th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Type</th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Quantity</th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Cost/Unit</th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Total Cost</th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Notes</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {currentItemTransactions.map((txn) => (
                      <tr key={txn._id} className="hover:bg-gray-50 transition-colors duration-150">
                        <td className="px-6 py-4 text-sm text-gray-700">{new Date(txn.transactionDate).toLocaleDateString()}</td>
                        <td className="px-6 py-4">
                          {txn.transactionType === 'receipt' ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                              <FiTrendingUp size={12} />
                              Receipt
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-800 rounded-full text-xs font-medium">
                              <FiTrendingDown size={12} />
                              Issue
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700">{txn.quantity} {txn.unit}</td>
                        <td className="px-6 py-4">
                          {txn.priceChange !== 0 ? (
                            <div className="flex items-center gap-2">
                              <span
                                style={{
                                  color: txn.priceChange > 0 ? '#ef4444' : '#22c55e',
                                  fontWeight: '600'
                                }}
                              >
                                ₹{txn.costPerUnit}
                              </span>
                              {txn.priceChange > 0 ? (
                                <span style={{ color: '#ef4444', fontSize: '12px' }}>
                                  ↑ +₹{Math.abs(txn.priceChange)}
                                </span>
                              ) : (
                                <span style={{ color: '#22c55e', fontSize: '12px' }}>
                                  ↓ -₹{Math.abs(txn.priceChange)}
                                </span>
                              )}
                            </div>
                          ) : (
                            <span style={{ color: '#666' }}>₹{txn.costPerUnit}</span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm font-bold text-gray-900">₹{txn.totalCost?.toFixed(2)}</div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700">{txn.notes || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination for Item Transactions */}
              {itemTransactions.length > 0 && (
                <div className="bg-gray-50 rounded-lg p-4 mt-6">
                  <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <span>Show</span>
                      <select 
                        className="px-3 py-1 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none transition-colors duration-200" 
                        value={itemTransactionsPerPage}
                        onChange={(e) => {
                          setItemTransactionsPerPage(Number(e.target.value));
                          setItemTransactionPage(1);
                        }}
                      >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                      <span>entries</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button 
                        className="px-3 py-1 bg-gray-200 hover:bg-gray-300 disabled:bg-gray-100 disabled:text-gray-400 text-gray-700 rounded-lg font-medium transition-colors duration-200" 
                        onClick={() => paginateItemTransactions(itemTransactionPage - 1)}
                        disabled={itemTransactionPage === 1}
                      >
                        Previous
                      </button>
                      <span className="px-3 py-1 text-sm text-gray-600">Page {itemTransactionPage} of {totalItemTransactionPages}</span>
                      <button 
                        className="px-3 py-1 bg-gray-200 hover:bg-gray-300 disabled:bg-gray-100 disabled:text-gray-400 text-gray-700 rounded-lg font-medium transition-colors duration-200" 
                        onClick={() => paginateItemTransactions(itemTransactionPage + 1)}
                        disabled={itemTransactionPage === totalItemTransactionPages}
                      >
                        Next
                      </button>
                    </div>
                    <div className="text-sm text-gray-600">
                      <span>Total: {itemTransactions.length} transactions (Last 7 days)</span>
                    </div>
                  </div>
                </div>
              )}
              
              {itemTransactions.length === 0 && (
                <div className="bg-gray-50 rounded-lg p-12 text-center">
                  <div className="w-20 h-20 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                    <FiCalendar className="text-gray-400 text-3xl" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No Transactions Found</h3>
                  <p className="text-gray-600">No transactions found for this item in the last 7 days.</p>
                </div>
              )}
            </div>
            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end">
              <button 
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium transition-colors duration-200"
                onClick={() => setShowTransactionsModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Category Modal */}
      {showCategoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="border-b border-gray-200 px-6 py-4">
              <h2 className="text-xl font-semibold text-gray-900">{selectedCategory ? 'Edit Category' : 'Add Category'}</h2>
              <button 
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
                onClick={() => setShowCategoryModal(false)}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <form onSubmit={handleCategorySubmit}>
              <div className="px-6 py-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Category Name *</label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none transition-colors"
                    value={categoryForm.name}
                    onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                    required
                  />
                </div>
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700">Active</label>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={categoryForm.isActive}
                      onChange={(e) => setCategoryForm({ ...categoryForm, isActive: e.target.checked })}
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                  </label>
                </div>
              </div>
              <div className="border-t border-gray-200 px-6 py-4 flex justify-end space-x-3">
                <button 
                  type="button" 
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                  onClick={() => setShowCategoryModal(false)}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="px-4 py-2 bg-purple-100 hover:bg-purple-200 text-purple-700 rounded-lg transition-colors font-medium"
                >
                  {selectedCategory ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Item History Modal */}
      {showItemHistoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gray-50">
              <h2 className="text-xl font-bold text-gray-800">History - {selectedItemName}</h2>
              <button 
                className="text-gray-500 hover:text-gray-700 text-2xl font-bold leading-none transition-colors" 
                onClick={() => setShowItemHistoryModal(false)}
              >
                ×
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gradient-to-r from-blue-50 to-blue-100 border-b border-gray-200 sticky top-0 z-10">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Date</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Type</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Quantity</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Previous Qty</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">New Qty</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Stock Change</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Cost/Unit</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Total Cost</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Notes</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {selectedItemHistory.slice((historyCurrentPage - 1) * historyItemsPerPage, historyCurrentPage * historyItemsPerPage).map((history, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-6 py-4 text-sm text-gray-900">
                            {new Date(history.transactionDate).toLocaleDateString('en-IN', {
                              day: '2-digit',
                              month: '2-digit', 
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                              history.transactionType === 'purchase' 
                                ? 'bg-green-100 text-green-800' 
                                : history.transactionType === 'sale'
                                ? 'bg-red-100 text-red-800'
                                : 'bg-blue-100 text-blue-800'
                            }`}>
                              {history.transactionType === 'purchase' ? 'Purchase' : 
                               history.transactionType === 'sale' ? 'Sale' : 'Adjustment'}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">{Number(history.quantity).toFixed(2)}</td>
                          <td className="px-6 py-4 text-sm text-gray-600">{Number(history.previousQuantity).toFixed(2)}</td>
                          <td className="px-6 py-4 text-sm font-medium text-gray-900">{Number(history.newQuantity).toFixed(2)}</td>
                          <td className="px-6 py-4">
                            <div className="space-y-1">
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                history.newQuantity > history.previousQuantity 
                                  ? 'bg-green-100 text-green-800' 
                                  : history.newQuantity < history.previousQuantity
                                  ? 'bg-red-100 text-red-800'
                                  : 'bg-gray-100 text-gray-800'
                              }`}>
                                {history.newQuantity > history.previousQuantity 
                                  ? `+${(history.newQuantity - history.previousQuantity).toFixed(2)}` 
                                  : history.newQuantity < history.previousQuantity
                                  ? `${(history.newQuantity - history.previousQuantity).toFixed(2)}`
                                  : '0.00'
                                }
                              </span>
                              <div className="text-xs text-gray-500">
                                {Number(history.previousQuantity).toFixed(2)} → {Number(history.newQuantity).toFixed(2)}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">₹{Number(history.costPerUnit).toFixed(2)}</td>
                          <td className="px-6 py-4 text-sm text-gray-900">₹{Number(history.totalCost).toFixed(2)}</td>
                          <td className="px-6 py-4 text-sm text-gray-700 break-words max-w-lg">{history.notes || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              
              {/* Pagination for Item History */}
              {selectedItemHistory.length > 0 && (
                <div className="bg-gray-50 rounded-lg p-4 mt-6">
                  <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <span>Show</span>
                      <select
                        value={historyItemsPerPage}
                        onChange={(e) => {
                          setHistoryItemsPerPage(Number(e.target.value));
                          setHistoryCurrentPage(1);
                        }}
                        className="px-3 py-1 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
                      >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                      </select>
                      <span>entries</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        className="px-3 py-1 bg-gray-200 hover:bg-gray-300 disabled:bg-gray-100 disabled:text-gray-400 text-gray-700 rounded-lg font-medium transition-colors"
                        onClick={() => setHistoryCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={historyCurrentPage === 1}
                      >
                        Previous
                      </button>
                      <span className="px-3 py-1 text-sm text-gray-600">
                        Page {historyCurrentPage} of {Math.ceil(selectedItemHistory.length / historyItemsPerPage)}
                      </span>
                      <button
                        className="px-3 py-1 bg-gray-200 hover:bg-gray-300 disabled:bg-gray-100 disabled:text-gray-400 text-gray-700 rounded-lg font-medium transition-colors"
                        onClick={() => setHistoryCurrentPage(prev => Math.min(Math.ceil(selectedItemHistory.length / historyItemsPerPage), prev + 1))}
                        disabled={historyCurrentPage === Math.ceil(selectedItemHistory.length / historyItemsPerPage)}
                      >
                        Next
                      </button>
                    </div>
                    <div className="text-sm text-gray-600">
                      <span>Total: {selectedItemHistory.length} records</span>
                    </div>
                  </div>
                </div>
              )}
              
              {selectedItemHistory.length === 0 && (
                <div className="bg-gray-50 rounded-lg p-12 text-center">
                  <div className="w-20 h-20 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                    <FiCalendar className="text-gray-400 text-3xl" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No History Found</h3>
                  <p className="text-gray-600">No transaction history found for this item.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InventoryManagement;
